import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Grid } from '@mui/material';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import FolderIcon from '@mui/icons-material/Folder';
import SearchIcon from '@mui/icons-material/Search';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import { OnboardingStep } from './OnboardingStep';

interface OnboardingProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

export const Onboarding = ({ open, onComplete, onSkip }: OnboardingProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const handleGetStarted = () => {
    onComplete();
    navigate('/projects/new');
  };

  const steps = [
    {
      icon: <FolderIcon fontSize="large" />,
      title: t('onboarding.step1.title', 'Create a Project'),
      description: t(
        'onboarding.step1.description',
        'Organize your podcast episodes by language and learning goals. Projects help you track progress.'
      ),
    },
    {
      icon: <SearchIcon fontSize="large" />,
      title: t('onboarding.step2.title', 'Find Podcasts'),
      description: t(
        'onboarding.step2.description',
        'Search for any podcast or browse popular ones used by other learners. Add episodes to your projects.'
      ),
    },
    {
      icon: <HeadphonesIcon fontSize="large" />,
      title: t('onboarding.step3.title', 'Start Learning'),
      description: t(
        'onboarding.step3.description',
        'Listen, create segments to loop, get transcriptions/translations, and build your vocabulary list.'
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onSkip}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ textAlign: 'center', pt: 2 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            🎧 {t('onboarding.welcome', 'Welcome to Scribelate!')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('onboarding.subtitle', 'Learn languages through podcasts you love')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3 }}>
            {t('onboarding.howItWorks', 'How Scribelate Works')}
          </Typography>

          <Grid container spacing={3}>
            {steps.map((step, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={index}>
                <OnboardingStep
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  stepNumber={index + 1}
                />
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              mt: 4,
              p: 2,
              bgcolor: 'primary.light',
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="primary.contrastText">
              💡 {t('onboarding.tip', 'Tip: You can select any word in transcriptions to add it to your vocabulary for spaced repetition learning!')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
        <Button onClick={onSkip} color="inherit">
          {t('onboarding.skip', 'Skip Tutorial')}
        </Button>
        <Button
          onClick={handleGetStarted}
          variant="contained"
          size="large"
          sx={{ minWidth: 200 }}
        >
          {t('onboarding.getStarted', 'Get Started - Create First Project')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
